import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { InstructorFilter, InstructorFilters } from '@modules/instructor/models/instructor-filter';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TRANSLATION_SCOPE } from '@modules/instructor/i18n/scope';
import { isEmpty, isEqual } from 'lodash';
import { isFalsyValue } from '@shared/utils/object';
import { FilterSearchComponent } from '@shared/components/filter-search/filter-search.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { FilterBottomSheetComponent } from '@core/components/filter-bottom-sheet/filter-bottom-sheet.component';
import {
  BottomSheetFilter,
  CheckList,
  CheckOption,
  Search,
  SelectedCheckOptions
} from '@core/models/bottom-sheet-filter';
import { TranslocoService } from '@ngneat/transloco';
import { CourseFilters } from '@modules/course/all-course/models/course-filters';

@Component({
  selector: 'app-instructors-filter',
  templateUrl: './instructors-filter.component.html',
  styleUrls: ['./instructors-filter.component.scss']
})
export class InstructorsFilterComponent implements OnInit {
  @ViewChild(FilterSearchComponent) filterSearchComponent: FilterSearchComponent;

  @Input() instructorFilter: InstructorFilter;
  @Input() regions: Array<any>;
  @Input() templates: Array<any>;
  @Input() categories: Array<any>;
  @Output() changeInstructorFilter = new EventEmitter<InstructorFilter>();

  activeFilterCount: number = 0;
  isSearchResetting: boolean = false;
  selectedTemplates: string[] = [];
  selectedRegions: string[] = [];
  selectedCategories: string[] = [];
  searchFormControl = new FormControl('');
  translationScope = TRANSLATION_SCOPE;
  isFiltersLoaded: boolean;

  isBottomSheetFilterUpdated: boolean = false;
  bottomSheetFilters: CheckList[] = [];
  bottomSheetFilterOptions: CheckOption[];

  constructor(
    private translationService: TranslocoService,
    private bottomSheet: MatBottomSheet
  ) { }

  ngOnInit(): void {
    this.trackSearchFormControlValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes?.instructorFilter && changes?.instructorFilter?.currentValue && changes.instructorFilter?.isFirstChange()) {
      this.selectedTemplates = isEmpty(changes?.instructorFilter?.currentValue?.templates) ? [] : changes.instructorFilter.currentValue.templates.split(',');
      this.selectedRegions = isEmpty(changes?.instructorFilter?.currentValue?.regions) ? [] : changes.instructorFilter.currentValue.regions.split(',');
      this.selectedCategories = isEmpty(changes?.instructorFilter?.currentValue?.categories) ? []: changes.instructorFilter.currentValue.categories.split(',');
      this.searchFormControl.patchValue(changes.instructorFilter.currentValue.search);
    }

    if (!isFalsyValue(this.templates) && !isFalsyValue(this.categories) && !isFalsyValue(this.regions) && !this.isFiltersLoaded) {
      this.setBottomSheetFilters();
      this.isFiltersLoaded = true;
    }

    this.getActiveFilterCount();
  }

  updateInstructorFilters(instructorFilter: InstructorFilter): void {
    this.changeInstructorFilter.emit(instructorFilter);
  }

  updateTemplatesFilter(): void {
    if(this.isSelectedFilterValueChange(this.selectedTemplates,this.instructorFilter.templates.split(','))) {
      const updatedInstructorTemplatesFilter = {
        ...this.instructorFilter,
        templates: this.selectedTemplates.slice().join(',').trim()
      } as InstructorFilter;
      this.updateInstructorFilters(updatedInstructorTemplatesFilter);
      }
  }

  updateRegionsFilter(): void {
    if(this.isSelectedFilterValueChange(this.selectedRegions,this.instructorFilter.regions.split(','))) {
      const updatedInstructorTemplatesFilter = {
        ...this.instructorFilter,
        regions: this.selectedRegions.join(',').trim()
      } as InstructorFilter;
      this.updateInstructorFilters(updatedInstructorTemplatesFilter);
    }
  }

  updateCategoriesFilter(): void {
    if(this.isSelectedFilterValueChange(this.selectedCategories,this.instructorFilter.categories.split(','))) {
      const updatedInstructorTemplatesFilter = {
        ...this.instructorFilter,
        categories: this.selectedCategories.join(',').trim()
      } as InstructorFilter;
      this.updateInstructorFilters(updatedInstructorTemplatesFilter);
    }
  }

  clearTemplatesFilter(): void {
    if (this.instructorFilter.templates.length > 0) {
      const updatedInstructorTemplatesFilter = {...this.instructorFilter, templates: ''} as InstructorFilter;
      this.updateInstructorFilters(updatedInstructorTemplatesFilter);
    }
  }

  clearRegionsFilter(): void {
    if (this.instructorFilter.regions.length > 0) {
      const updatedInstructorTemplatesFilter = {...this.instructorFilter, regions: ''} as InstructorFilter;
      this.updateInstructorFilters(updatedInstructorTemplatesFilter);
    }
  }

  clearCategoriesFilter(): void {
    if(this.instructorFilter.categories.length> 0) {
      const updatedInstructorTemplatesFilter = {...this.instructorFilter, categories: ''} as InstructorFilter;
      this.updateInstructorFilters(updatedInstructorTemplatesFilter);
    }
  }

  updateSearchFilter(searchValue: string = ''): void {
    this.searchFormControl.patchValue(searchValue);
    this.instructorFilter = { ...this.instructorFilter, search: searchValue };
  }

  resetFiltersAndSearch() {
    if (isFalsyValue(this.selectedRegions) &&
      isFalsyValue(this.selectedCategories) &&
      isFalsyValue(this.selectedTemplates) &&
      isFalsyValue(this.searchFormControl.value) &&
      isFalsyValue(this.instructorFilter.search)
    ) {
      return;
    }

    this.filterSearchComponent.reset();
    this.selectedTemplates= [];
    this.selectedRegions= [];
    this.selectedCategories= [];
    this.isSearchResetting = true;
    this.searchFormControl.reset('');

    this.changeInstructorFilter.emit(new InstructorFilter());
  }

  openFilterBottomSheet(): void {
    this.getBottomSheetFilters();

    this.bottomSheet.open(FilterBottomSheetComponent,
      {
        data: {
          filters: this.bottomSheetFilters,
          search: {
            placeholder: this.translationService.translate(`${this.translationScope}.filters.search`),
            value: this.instructorFilter.search ?? '' } as Search
        } as BottomSheetFilter,
        disableClose: true
      }).afterDismissed()
      .subscribe((filters: BottomSheetFilter) => {
        if (filters.resetAll) {
          this.resetFiltersAndSearch();
        } else {
          this.updateFiltersByBottomSheetSelection(this.getBottomSheetFilterSelectedOptions(filters.filters), filters.search.value);
          this.fetchBottomSheetFilteredInstructors();
        }
      });
  }

  private trackSearchFormControlValue(): void {
    this.searchFormControl.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe((searchText: string) => {
      if (!this.isSearchResetting) {
        const updatedInstructorSearchFilter = {...this.instructorFilter, search: searchText} as InstructorFilter;
        this.updateInstructorFilters(updatedInstructorSearchFilter);
      } else {
        this.isSearchResetting = false;
      }
    });
  }

  private isSelectedFilterValueChange(currentValue: string[], previousValue: string[] = []): boolean {
    previousValue = previousValue.filter((value) => value !== '');
    if(isFalsyValue(currentValue)  && isFalsyValue(previousValue)) {
      return false
    }
    return (!isEqual(currentValue.sort(),previousValue.sort()));
  }

  private setBottomSheetFilters(): void {
    this.bottomSheetFilterOptions = this.templates.map((template) => ({
      id: template.id,
      label: template.title,
      selected: false
    }));
    this.bottomSheetFilters.push(this.getBottomSheetFilterOptions(InstructorFilters.TEMPLATE, this.translationService.translate(`${this.translationScope}.filters.templates`), this.bottomSheetFilterOptions));

    this.bottomSheetFilterOptions = this.regions.map((region) => ({
      id: region.id,
      label: region.name,
      selected: false
    }));
    this.bottomSheetFilters.push(this.getBottomSheetFilterOptions(InstructorFilters.REGION, this.translationService.translate(`${this.translationScope}.filters.regions`), this.bottomSheetFilterOptions));

    this.bottomSheetFilterOptions = this.categories.map((category) => ({
      id: category.id,
      label: category.title,
      selected: false
    }));
    this.bottomSheetFilters.push(this.getBottomSheetFilterOptions(InstructorFilters.CATEGORY, this.translationService.translate(`${this.translationScope}.filters.categories`), this.bottomSheetFilterOptions));
  }

  private getBottomSheetFilters(): void {
    this.bottomSheetFilters.find(filter => filter.name === InstructorFilters.TEMPLATE).options.forEach(option => {
      option.selected = this.selectedTemplates.includes(option.id);
    });
    this.bottomSheetFilters.find(filter => filter.name === InstructorFilters.REGION).options.forEach(option => {
      option.selected = this.selectedRegions.includes(option.id);
    });
    this.bottomSheetFilters.find(filter => filter.name === InstructorFilters.CATEGORY).options.forEach(option => {
      option.selected = this.selectedCategories.includes(option.id);
    });
  }

  private updateFiltersByBottomSheetSelection(selectedOptions: SelectedCheckOptions, searchText: string): void {
    if (this.isSelectedFilterValueChange(this.selectedTemplates, selectedOptions[InstructorFilters.TEMPLATE])) {
      this.isBottomSheetFilterUpdated = true;
      this.instructorFilter = {
        ...this.instructorFilter,
        templates: selectedOptions[CourseFilters.TEMPLATE].join(',')
      }
      this.selectedTemplates = selectedOptions[InstructorFilters.TEMPLATE];
    }

    if (this.isSelectedFilterValueChange(this.selectedRegions, selectedOptions[InstructorFilters.REGION])) {
      this.isBottomSheetFilterUpdated = true;
      this.instructorFilter = {
        ...this.instructorFilter,
        regions: selectedOptions[InstructorFilters.REGION].join(',')
      }
      this.selectedRegions = selectedOptions[InstructorFilters.REGION];
    }

    if (this.isSelectedFilterValueChange(this.selectedCategories, selectedOptions[InstructorFilters.CATEGORY])) {
      this.isBottomSheetFilterUpdated = true;
      this.instructorFilter = {
        ...this.instructorFilter,
        categories: selectedOptions[InstructorFilters.CATEGORY].join(',').trim()
      }
      this.selectedCategories = selectedOptions[InstructorFilters.CATEGORY];
    }

    if (this.instructorFilter.search !== searchText) {
      this.isBottomSheetFilterUpdated = true;
      this.instructorFilter = { ...this.instructorFilter, search: searchText };
    }
  }

  private getBottomSheetFilterSelectedOptions(filters: CheckList[]): SelectedCheckOptions {
    return filters.reduce((selectedIds, filter) => {
      selectedIds[filter.name] = Object.values(filter.options)
        .filter(option => option.selected)
        .map(option => option.id);
      return selectedIds;
    }, {} as SelectedCheckOptions);
  }

  private getBottomSheetFilterOptions(name: string, label: string, options: CheckOption[]): CheckList {
    return { name, label, options } as CheckList;
  }

  private fetchBottomSheetFilteredInstructors() {
    if (this.isBottomSheetFilterUpdated) {
      this.updateInstructorFilters(this.instructorFilter);
    }
    this.isBottomSheetFilterUpdated = false;
  }

  private getActiveFilterCount(): void {
    this.activeFilterCount = 0;
    if (!isFalsyValue(this.instructorFilter.search)) {
      this.activeFilterCount++;
    }

    if (!isFalsyValue(this.instructorFilter.regions)) {
      this.activeFilterCount++;
    }

    if (!isFalsyValue(this.instructorFilter.categories)) {
      this.activeFilterCount++;
    }

    if (!isFalsyValue(this.instructorFilter.templates)) {
      this.activeFilterCount++;
    }
  }
}
